import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevice } from '../../context/DeviceContext';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchOperationsData } from '../../store/slices/operationsSlice';

type OperationType = 'recargas' | 'paquetes' | 'facturas' | 'seguros';

interface OperationRedirectorProps {
  operationType: OperationType;
}

const ROUTES: Record<OperationType, { steps: string; normal: string }> = {
  recargas: { steps: '/operaciones/recargas-wizard', normal: '/operaciones/recargas' },
  paquetes: { steps: '/operaciones/paquetes-wizard', normal: '/operaciones/paquetes' },
  facturas: { steps: '/operaciones/facturas-wizard', normal: '/operaciones/facturas' },
  seguros:  { steps: '/operaciones/seguros-wizard',  normal: '/operaciones/seguros' },
};

/**
 * Redirige a la versión correcta de la página de operación basándose en:
 *   - operationMode = 'steps'  → siempre pasos (teclado táctil)
 *   - operationMode = 'normal' → siempre formulario clásico (teclado del sistema)
 *   - operationMode = 'auto'   → detecta el dispositivo (mobile/tablet → pasos, desktop → normal)
 */
export const OperationRedirector: React.FC<OperationRedirectorProps> = ({ operationType }) => {
  const { needsStepByStepInterface } = useDevice();
  const navigate   = useNavigate();
  const dispatch   = useAppDispatch();
  const { rechargeMethods, packetMethods, invoiceMethods, insuranceMethods } =
    useAppSelector((state) => state.operations as any);
  const { operationMode } = useAppSelector((state) => (state as any).settings);

  useEffect(() => {
    const loaded =
      rechargeMethods.length > 0 || packetMethods.length > 0 ||
      invoiceMethods.length > 0  || insuranceMethods.length > 0;
    if (!loaded) dispatch(fetchOperationsData());

    const routes = ROUTES[operationType];
    let useSteps: boolean;

    if (operationMode === 'steps') {
      useSteps = true;
    } else if (operationMode === 'normal') {
      useSteps = false;
    } else {
      // 'auto': usa la detección de dispositivo
      useSteps = needsStepByStepInterface;
    }

    navigate(useSteps ? routes.steps : routes.normal, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationType, operationMode, needsStepByStepInterface]);

  return null;
};
